import { useState, useEffect } from 'react'
import Button from '../../../components/common/Button/Button'
import {
  getAllEmployeesForLeaveEdit,
  getCustomLeaveBalance,
  getApprovedLeaveDays,
  updateCustomLeaveBalance
} from '../../../services/timeOffService'

const EditLeaveBalanceModal = ({ isOpen, onClose, onSuccess }) => {
  const [employees, setEmployees] = useState([])
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('')
  const [currentBalance, setCurrentBalance] = useState(0)
  const [newBalance, setNewBalance] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (isOpen) {
      loadEmployees()
    }
  }, [isOpen])

  useEffect(() => {
    if (selectedEmployeeId) {
      loadCurrentBalance()
    }
  }, [selectedEmployeeId])

  const loadEmployees = async () => {
    setIsLoading(true)
    const result = await getAllEmployeesForLeaveEdit()
    if (result.success) {
      setEmployees(result.data)
      if (result.data.length > 0) {
        setSelectedEmployeeId(result.data[0].employeeId)
      }
    }
    setIsLoading(false)
  }

  const loadCurrentBalance = async () => {
    if (!selectedEmployeeId) return

    const selectedEmployee = employees.find(emp => emp.employeeId === selectedEmployeeId)
    if (!selectedEmployee) return

    try {
      // Check for custom balance first
      const customBalance = await getCustomLeaveBalance(selectedEmployee.employeeId, selectedEmployee.uid)
      if (customBalance !== null) {
        setCurrentBalance(customBalance)
        setNewBalance(customBalance.toString())
        return
      }

      // Calculate from approved days
      const approvedDays = await getApprovedLeaveDays(selectedEmployee.employeeId)
      const balance = Math.max(0, 15 - approvedDays)
      setCurrentBalance(balance)
      setNewBalance(balance.toString())
    } catch (error) {
      console.error('Error loading current balance:', error)
    }
  }

  const handleSave = async () => {
    setError('')

    // Validate input
    const balanceNum = parseFloat(newBalance)
    if (isNaN(balanceNum) || balanceNum < 0 || balanceNum > 365) {
      setError('Please enter a valid balance (0-365 days)')
      return
    }

    setIsSaving(true)

    try {
      const selectedEmployee = employees.find(emp => emp.employeeId === selectedEmployeeId)
      if (!selectedEmployee) {
        setError('Please select a valid employee')
        setIsSaving(false)
        return
      }

      const result = await updateCustomLeaveBalance(
        selectedEmployee.employeeId,
        balanceNum,
        selectedEmployee.uid
      )

      if (result.success) {
        onSuccess && onSuccess()
        onClose()
      } else {
        setError(result.error || 'Failed to update leave balance')
      }
    } catch (error) {
      console.error('Save error:', error)
      setError('An error occurred while saving')
    } finally {
      setIsSaving(false)
    }
  }

  const handleClose = () => {
    if (!isSaving) {
      setError('')
      setNewBalance('')
      setSelectedEmployeeId('')
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem'
      }}
      onClick={handleClose}
    >
      <div
        style={{
          background: 'var(--psyber-card-bg)',
          borderRadius: 'var(--psyber-radius-lg)',
          border: '2px solid var(--psyber-primary)',
          maxWidth: '500px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '1.5rem',
            borderBottom: '2px solid var(--psyber-card-border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: 'var(--psyber-font-size-xl)',
              color: 'var(--psyber-text)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <i className="fas fa-edit"></i>
            Edit Leave Balance
          </h3>
          <button
            onClick={handleClose}
            disabled={isSaving}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: '1.5rem',
              color: 'var(--psyber-text-secondary)',
              cursor: 'pointer',
              padding: '0.25rem',
              transition: 'color 0.2s ease'
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--psyber-error)')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--psyber-text-secondary)')}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '1.5rem' }}>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <i
                className="fas fa-spinner fa-spin"
                style={{ fontSize: '2rem', color: 'var(--psyber-primary)' }}
              ></i>
              <p style={{ marginTop: '1rem', color: 'var(--psyber-text-secondary)' }}>
                Loading employees...
              </p>
            </div>
          ) : (
            <>
              {/* Employee Select */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label
                  htmlFor="employee-select"
                  style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: 'var(--psyber-font-size-sm)',
                    fontWeight: 600,
                    color: 'var(--psyber-text)'
                  }}
                >
                  Select Employee
                </label>
                <select
                  id="employee-select"
                  value={selectedEmployeeId}
                  onChange={(e) => setSelectedEmployeeId(e.target.value)}
                  className="psyber-input"
                  style={{ width: '100%' }}
                  disabled={isSaving}
                >
                  <option value="">-- Select Employee --</option>
                  {employees.map((emp) => (
                    <option key={emp.employeeId} value={emp.employeeId}>
                      {emp.fullName} ({emp.employeeId})
                    </option>
                  ))}
                </select>
              </div>

              {selectedEmployeeId && (
                <>
                  {/* Current Balance Display */}
                  <div
                    style={{
                      marginBottom: '1.5rem',
                      padding: '1rem',
                      background: 'var(--psyber-info-bg)',
                      border: '1px solid var(--psyber-info)',
                      borderRadius: 'var(--psyber-radius-md)'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                      <i className="fas fa-info-circle" style={{ color: 'var(--psyber-info)' }}></i>
                      <span style={{ fontSize: 'var(--psyber-font-size-sm)', fontWeight: 600, color: 'var(--psyber-info)' }}>
                        Current Balance
                      </span>
                    </div>
                    <p style={{ margin: 0, fontSize: 'var(--psyber-font-size-lg)', fontWeight: 700, color: 'var(--psyber-text)' }}>
                      {currentBalance} days
                    </p>
                  </div>

                  {/* New Balance Input */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label
                      htmlFor="new-balance"
                      style={{
                        display: 'block',
                        marginBottom: '0.5rem',
                        fontSize: 'var(--psyber-font-size-sm)',
                        fontWeight: 600,
                        color: 'var(--psyber-text)'
                      }}
                    >
                      New Balance (days)
                    </label>
                    <input
                      id="new-balance"
                      type="number"
                      min="0"
                      max="365"
                      step="1"
                      value={newBalance}
                      onChange={(e) => setNewBalance(e.target.value)}
                      className="psyber-input"
                      style={{ width: '100%' }}
                      placeholder="Enter new balance (0-365)"
                      disabled={isSaving}
                    />
                    <p style={{ margin: '0.5rem 0 0 0', fontSize: 'var(--psyber-font-size-xs)', color: 'var(--psyber-text-secondary)' }}>
                      Enter a number between 0 and 365 days
                    </p>
                  </div>
                </>
              )}

              {/* Error Message */}
              {error && (
                <div
                  style={{
                    marginBottom: '1.5rem',
                    padding: '0.75rem 1rem',
                    background: 'var(--psyber-error-bg)',
                    border: '1px solid var(--psyber-error)',
                    borderRadius: 'var(--psyber-radius-md)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <i className="fas fa-exclamation-circle" style={{ color: 'var(--psyber-error)' }}></i>
                  <span style={{ fontSize: 'var(--psyber-font-size-sm)', color: 'var(--psyber-error)' }}>
                    {error}
                  </span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '1.5rem',
            borderTop: '2px solid var(--psyber-card-border)',
            display: 'flex',
            gap: '1rem',
            justifyContent: 'flex-end'
          }}
        >
          <Button variant="ghost" onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!selectedEmployeeId || !newBalance || isSaving}
            loading={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Balance'}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default EditLeaveBalanceModal
